// DLG-136 Edit User and the staff Users page's Add User (legacy pages/admin/users/UsersItem.vue, opened from Account
// Detail's account users, the staff location page's users, and the staff Users page; field notes backoffice.accounts.detail
// and backoffice.admin.users.list): Basic Info (Name, Email, Phone, Time Zone) and Password (Password, Confirm Password;
// blank keeps the current one on an edit, and is required on a create); Cancel, Update or Create.
//   - From an account: Radish admins and resellers (`identity.updateUser`); a reseller is refused a user who also
//     belongs to another reseller's account (B213).
//   - From the staff Users page: Radish admins only (`identity.createPlatformUser`, `identity.updatePlatformUser`),
//     for any user whatever accounts they belong to. A created user belongs to no account, as legacy's.
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Dialog, SECTION_HEADING, SelectField, TextField, useToast } from '@radish/ui-web';
import { unwrap, useApi, useLocationQuery, type CallFailure } from '../../data/api.tsx';
import { uuidv7 } from '../../lib/ids.ts';
import { phoneText, zoneLabel } from '../branding/LocationInfoPage.tsx';
import { PasswordField } from '../auth/fields.tsx';
import { emailErrors } from '../../lib/email.ts';

const ZONES = ['America/Los_Angeles', 'America/Denver', 'America/Chicago', 'America/New_York', 'Pacific/Honolulu'] as const;
type Zone = (typeof ZONES)[number];

interface Form {
  readonly name: string;
  readonly email: string;
  readonly phone: string;
  readonly timeZone: Zone | null;
  readonly password: string;
  readonly confirm: string;
}

const BLANK: Form = { name: '', email: '', phone: '', timeZone: null, password: '', confirm: '' };
type Errors = Partial<Record<keyof Form, string>>;

/** Legacy UsersItem.vue's rules: a password is required only on a create (Add User). */
function check(f: Form, passwordRequired: boolean): Errors {
  const email = f.email.trim();
  const phone = f.phone.replace(/\D/g, '');
  return {
    ...(f.name.trim() === '' ? { name: 'Please type a name' } : {}),
    ...emailErrors(email),
    ...(phone !== '' && phone.length !== 10 ? { phone: 'Phone number must be 10 digits' } : {}),
    ...(passwordRequired && f.password === '' ? { password: 'Please enter password' } : {}),
    ...((passwordRequired || f.password !== '') && f.confirm === '' ? { confirm: 'Please enter password' } : f.confirm !== f.password ? { confirm: 'Passwords do not match' } : {}),
  };
}

/** What a save sends: the trimmed fields, the phone's digits, and the time zone and password only when given. */
interface UserFields {
  readonly name: string;
  readonly email: string;
  readonly phone: string | null;
  readonly timeZone?: Zone;
  readonly password?: string;
}

function fieldsOf(f: Form): UserFields {
  const phone = f.phone.replace(/\D/g, '');
  return {
    name: f.name.trim(), email: f.email.trim().toLowerCase(), phone: phone === '' ? null : phone,
    ...(f.timeZone === null ? {} : { timeZone: f.timeZone }), ...(f.password === '' ? {} : { password: f.password }),
  };
}

interface Loaded { readonly name: string; readonly email: string; readonly phone: string | null; readonly timeZone: string | null }

const formOf = (u: Loaded): Form => ({
  name: u.name, email: u.email, phone: u.phone ?? '', timeZone: (ZONES as readonly string[]).includes(u.timeZone ?? '') ? (u.timeZone as Zone) : null,
  password: '', confirm: '',
});

interface UserDialogProps {
  readonly open: boolean;
  /** Add User (blank form, password required) or Edit User (filled from `loaded`). */
  readonly creating: boolean;
  readonly loaded: Loaded | undefined;
  readonly save: (fields: UserFields, commandId: string) => Promise<unknown>;
  readonly onClose: () => void;
}

function UserDialog({ open, creating, loaded, save: send, onClose }: UserDialogProps) {
  const toast = useToast();
  const { environment } = useApi();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<Form>(BLANK);
  const [errors, setErrors] = useState<Errors>({});

  useEffect(() => {
    if (!open || creating || loaded === undefined) return;
    setForm(formOf(loaded));
  }, [open, creating, loaded]);

  const save = useMutation<unknown, CallFailure, Form>({ mutationFn: (f) => send(fieldsOf(f), uuidv7()) });

  const close = () => {
    setForm(BLANK);
    setErrors({});
    save.reset();
    onClose();
  };

  const submit = () => {
    // Legacy's q-form is not `greedy`: it stops at the first field that fails, top to bottom, and marks only that one
    // (shot backoffice.admin.users.list-dialog-create-required: an empty Add User marks Name alone).
    const [first] = Object.entries(check(form, creating));
    setErrors(first === undefined ? {} : { [first[0]]: first[1] });
    if (first !== undefined) return;
    save.mutate(form, {
      onSuccess: async () => {
        toast.success(creating ? 'User created successfully' : 'User updated successfully');
        await queryClient.invalidateQueries({ queryKey: [environment.profile] });
        close();
      },
      onError: (e) => (e.code === 'identity.email_taken' ? setErrors({ email: e.message }) : e.code === 'identity.password_invalid' ? setErrors({ password: e.message }) : toast.error(e.message)),
    });
  };

  const set = (key: keyof Form) => (value: string) => setForm((f) => ({ ...f, [key]: value }));
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => { if (!o) close(); }}
      title={creating ? 'Add User' : 'Edit User'}
      // Legacy's Edit User dialog is 560 wide (lane V r8 measured ours at the default 740).
      width={560}
      testId="edit-user-dialog"
      footer={
        <>
          <Button variant="outline" onClick={close}>Cancel</Button>
          <Button onClick={submit} loading={save.isPending} disabled={!creating && loaded === undefined}>{creating ? 'Create' : 'Update'}</Button>
        </>
      }
    >
      {/* Legacy UsersItem.vue's UiSectionHeadings (q-mb-md): 21.6 px lines, the gap-4 standing in for their 16 px margin (B1339). */}
      <form className="flex flex-col gap-4" autoComplete="off" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <h3 className={SECTION_HEADING}>Basic Info</h3>
        <TextField label="Name" value={form.name} onValueChange={set('name')} error={errors.name} />
        <TextField label="Email" type="email" value={form.email} onValueChange={set('email')} error={errors.email} />
        <TextField label="Phone" inputMode="tel" value={phoneText(form.phone.replace(/\D/g, ''))} onValueChange={(v) => set('phone')(v.replace(/\D/g, '').slice(0, 10))} error={errors.phone} />
        <SelectField label="Time Zone" value={form.timeZone} options={ZONES.map((z) => ({ value: z, label: zoneLabel(z) }))} onValueChange={(z) => setForm((f) => ({ ...f, timeZone: z }))} testId="edit-user-timezone" />
        <h3 className={SECTION_HEADING}>Password</h3>
        <PasswordField mutedEye label="Password" autoComplete="new-password" value={form.password} onValueChange={set('password')} error={errors.password} />
        <PasswordField mutedEye label="Confirm Password" autoComplete="new-password" value={form.confirm} onValueChange={set('confirm')} error={errors.confirm} />
        <button type="submit" hidden aria-hidden tabIndex={-1} />
      </form>
    </Dialog>
  );
}

/** DLG-136 from an account (Account Detail, the staff location page): a user of that account. */
export function EditUserDialog({ accountId, userId, onClose }: { readonly accountId: string; readonly userId: string | null; readonly onClose: () => void }) {
  const { client } = useApi();
  const loaded = useLocationQuery(accountId, 'identity.accountUser', { userId }, (c) => c.queries.identity.accountUser({ userId: userId as never }), userId !== null).data;
  return (
    <UserDialog
      open={userId !== null}
      creating={false}
      loaded={loaded}
      save={(fields, commandId) => unwrap(() => client.commands.identity.updateUser({ accountId: accountId as never, userId: userId as never, ...fields }, { commandId }))}
      onClose={onClose}
    />
  );
}

/**
 * The staff Users page's Create User and row edit (SCR-012): Radish admins only. `editing` is `'new'` for Add User, a
 * user id for Edit User, or null when closed.
 */
export function PlatformUserDialog({ editing, onClose }: { readonly editing: string | 'new' | null; readonly onClose: () => void }) {
  const { client } = useApi();
  const userId = editing === null || editing === 'new' ? null : editing;
  const loaded = useLocationQuery('platform', 'identity.platformUser', { userId }, (c) => c.queries.identity.platformUser({ userId: userId as never }), userId !== null).data;
  return (
    <UserDialog
      open={editing !== null}
      creating={editing === 'new'}
      loaded={loaded}
      save={(fields, commandId) => (userId === null
        ? unwrap(() => client.commands.identity.createPlatformUser({ userId: uuidv7() as never, ...fields, password: fields.password ?? '' }, { commandId }))
        : unwrap(() => client.commands.identity.updatePlatformUser({ userId: userId as never, ...fields }, { commandId })))}
      onClose={onClose}
    />
  );
}
